import { cn } from "@repo/ui/lib/utils";
import type { ComponentProps } from "react";
import { ACCENTS, type TimelineAccent, type TimelineVariant, VARIANTS } from "./tokens";

const FILL = {
  strong: (a: (typeof ACCENTS)[TimelineAccent]) => a.fillStrong,
  base: (a: (typeof ACCENTS)[TimelineAccent]) => a.fill,
  soft: (a: (typeof ACCENTS)[TimelineAccent]) => a.fillSoft,
} as const;

export interface TimelineSurface {
  /** Status. Selects weights and edges; never a hue. */
  variant?: TimelineVariant;
  /** Identity — whose schedule this is. The only thing that selects a hue. */
  accent?: TimelineAccent;
}

/**
 * One unit of work as a tinted rectangle — the rounded box, its left rule and
 * its tint, and nothing else. Interior layout is the caller's, because the
 * surfaces disagree about it: a plan block is a fixed-height two-line stack, a
 * calendar tile reflows itself by container query at four different heights.
 */
export function timelineBlockSurface({ variant = "scheduled", accent = "primary" }: TimelineSurface) {
  const v = VARIANTS[variant];
  const a = ACCENTS[v.accent ?? accent];
  return cn(
    // One hue, two strengths: the frame sits at 50% so it reads as a quiet
    // edge, the left rule at full. Width separates them too, and the two widths
    // must stay far apart — at 2px/3px they read as one thick frame with no
    // rule at all, and anything heavier than 1px around weighs the card down.
    "overflow-hidden rounded-md border border-l-4",
    a.text,
    FILL[v.weight](a),
    // Only a genuinely faint block softens its rule; booked and delivered both
    // carry the full one, so the rule always reads as the block's own edge.
    v.weight === "soft" ? cn(a.lineSoft, a.ruleSoft) : cn(a.line, a.rule),
    v.dashed && "border-dashed",
    v.extra,
  );
}

/** The muted second line — the accent's own, so it never fights the fill. */
export function timelineSubtext({ variant = "scheduled", accent = "primary" }: TimelineSurface) {
  return ACCENTS[VARIANTS[variant].accent ?? accent].subtext;
}

export interface TimelineBlockProps extends Omit<ComponentProps<"div">, "color">, TimelineSurface {}

/** The surface with the plan timeline's interior: title row, detail, badge. */
export function TimelineBlock({ variant, accent, className, children, ...props }: TimelineBlockProps) {
  return (
    <div
      data-slot="timeline-block"
      className={cn(
        "flex min-h-0 min-w-0 shrink-0 flex-col justify-center gap-0.5 px-2.5 py-1.25 text-xs leading-4",
        timelineBlockSurface({ variant, accent }),
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

function TimelineBlockRow({ className, children, ...props }: ComponentProps<"div">) {
  return (
    <div className={cn("flex min-w-0 items-center justify-between gap-2", className)} {...props}>
      {children}
    </div>
  );
}

function TimelineBlockTitle({ className, children, ...props }: ComponentProps<"div">) {
  return (
    <div className={cn("min-w-0 truncate font-medium", className)} {...props}>
      {children}
    </div>
  );
}

function TimelineBlockDuration({ className, children, ...props }: ComponentProps<"div">) {
  return (
    <div className={cn("shrink-0 whitespace-nowrap tabular-nums opacity-70", className)} {...props}>
      {children}
    </div>
  );
}

function TimelineBlockDetail({ className, children, ...props }: ComponentProps<"div">) {
  return (
    <div className={cn("min-w-0 truncate text-[11px] opacity-70", className)} {...props}>
      {children}
    </div>
  );
}

function TimelineBlockLabel({ className, children, ...props }: ComponentProps<"span">) {
  return (
    <span
      className={cn("shrink-0 rounded bg-current/8 px-1 py-px font-medium text-[10px] leading-[14px]", className)}
      {...props}
    >
      {children}
    </span>
  );
}

TimelineBlock.Row = TimelineBlockRow;
TimelineBlock.Title = TimelineBlockTitle;
TimelineBlock.Duration = TimelineBlockDuration;
TimelineBlock.Detail = TimelineBlockDetail;
TimelineBlock.Label = TimelineBlockLabel;
